/**
 * "My Car" rendering — full dashboard layout.
 *
 * Renders into either `#accountContent` (drawer) or `#myCarSubModalContent`
 * (the sub-modal opened from the home feed card). Same DOM IDs as legacy so
 * CSS keeps working.
 *
 * Layout sections (top to bottom):
 *   1. Hero card    — color-tinted car icon, plate badge, model+year, ownership age
 *   2. Dashboard    — 4 status widgets (license / ownership / recalls / pollution)
 *   3. Recalls      — open manufacturer recalls list (collapses when empty)
 *   4. Banners      — license + ownership detailed banners (existing service)
 *   5. Details      — collapsible <details> with full gov fields
 *   6. Maintenance  — user-managed service log
 *   7. Stats        — simple per-user activity metrics
 *   8. Actions      — "open details modal" + "replace vehicle"
 */

import { auth, ref, ensureAuthReady } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { badgeHtml as plateBadgeHtml } from '../../shared/plate.js';
import { escape as esc } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';
import { ensureGovBundle, getLastOwnership } from './ownership.service.js';
import { render as renderBanners } from './licenseBanner.view.js';
import { getStatus as getLicenseStatus } from './license.service.js';
import * as bus from '../../core/eventBus.js';

const log = createLogger('myCar');

/**
 * @typedef {Object} MyCarModel
 * @property {string | null} plate
 * @property {string | null} year
 */

// ====================================================================
// Public API
// ====================================================================

/**
 * Render the "my car" UI into the given host element ID.
 * @param {string} hostId
 */
export async function renderInto(hostId) {
  const host = document.getElementById(hostId);
  if (!host) {
    log.warn(`target #${hostId} not found`);
    return;
  }

  try {
    await ensureAuthReady();
    const model = await loadModel();
    // hasCar depends on plate ONLY — year is supplementary metadata that
    // gets backfilled from the gov registry. The legacy 3-step login flow
    // captured production-year at signup so this used to be `plate && year`,
    // but the new SMS-only flow + register modal only collect ownership
    // month/year, leaving `users/{uid}/year` null on first registration.
    // Requiring `year` here would hide a just-registered car as "no car".
    const hasCar = !!model.plate;

    if (hostId === 'accountContent') {
      // The legacy 3-step login required a vehicle before the user could
      // even reach the home screen — so disabling the drawer-close buttons
      // when `!hasCar` was harmless (no logged-in user was ever in that
      // state). The new SMS-only login lets users be authenticated WITHOUT
      // a registered vehicle (they register one on first search via the
      // gate prompt). Keeping the buttons enabled at all times so they're
      // never trapped in the drawer.
      const closeX = document.getElementById('accountCloseX');
      const closeBtn = document.getElementById('accountCloseBtn');
      [closeX, closeBtn].forEach((el) => { if (el) /** @type {HTMLButtonElement} */ (el).disabled = false; });
    }

    if (!hasCar) {
      host.innerHTML = renderNoCar();
      wireButtons(host);
      return;
    }

    const plate = /** @type {string} */ (model.plate);

    // Load all dashboard data in parallel — the screen renders only once
    // everything is ready, so the user never sees half a card.
    await ensureGovBundle(plate).catch((e) => log.warn('gov bundle:', e));
    const [govBundle, ownership, license, recalls, maintenance, statsData] = await Promise.all([
      readGov(plate),
      getLastOwnership(plate).catch(() => ({ dateISO: null })),
      getLicenseStatus(plate).catch(() => ({ status: 'unknown', daysLeft: null })),
      loadOpenRecalls(plate),
      loadMaintenance(plate),
      loadStats(),
    ]);

    const bannersId = `${hostId}_banners`;
    host.innerHTML = renderHasCar({
      model, gov: govBundle, ownership, license, recalls, maintenance, stats: statsData, bannersId,
    });

    wireButtons(host);
    try { await renderBanners(bannersId); } catch (e) { log.warn('banners failed:', e); }
    wireRefreshButton(host, plate, bannersId);
    wireRecallActions(host, plate);
    wireMaintenanceForm(host, plate);

    setTimeout(() => {
      const w = /** @type {any} */ (window);
      if (typeof w.checkAlertStatusAndUpdateDot === 'function') w.checkAlertStatusAndUpdateDot();
    }, 500);
  } catch (err) {
    log.error('renderInto failed:', err);
  }
}

/**
 * Render into every host that exists in the DOM (account drawer + sub-modal).
 *
 * Both `#accountContent` and `#myCarSubModalContent` are present in
 * index.html at all times; rendering into both keeps them in sync.
 */
export async function renderSection() {
  const hostIds = ['accountContent', 'myCarSubModalContent']
    .filter((id) => !!document.getElementById(id));
  if (hostIds.length === 0) {
    log.warn('no DOM target for renderSection');
    return;
  }
  await Promise.all(hostIds.map((hostId) => renderInto(hostId)));
}

// ====================================================================
// Data loaders
// ====================================================================

/** @returns {Promise<MyCarModel>} */
async function loadModel() {
  const legacy = /** @type {any} */ (window).loadAccountModel;
  if (typeof legacy === 'function') {
    try {
      const m = await legacy();
      return { plate: m?.plate ?? null, year: m?.year ?? null };
    } catch { /* fall through */ }
  }
  const uid = auth().currentUser?.uid;
  if (!uid) return { plate: LocalState.plate, year: LocalState.carYear };
  /** @type {any} */
  const u = (await ref(`users/${uid}`).once('value')).val() ?? {};
  return {
    plate: u.plate ?? LocalState.plate,
    year: u.year ?? LocalState.carYear,
  };
}

async function readGov(plate) {
  try {
    const snap = await ref(`vehicles/${plate}/gov/vehicle`).once('value');
    return snap.val() || {};
  } catch (_) { return {}; }
}

/**
 * Return open recalls — those notified by the cloud function but not yet
 * marked handled by the owner.
 */
async function loadOpenRecalls(plate) {
  try {
    const snap = await ref(`vehicles/${plate}/recallsNotified`).once('value');
    const map = snap.val() || {};
    /** @type {Array<{ id: string, desc: string, website: string | null, sentAt: number }>} */
    const out = [];
    for (const [id, rec] of Object.entries(map)) {
      const r = /** @type {any} */ (rec);
      if (r && !r.markedHandledAt) {
        out.push({
          id,
          desc: String(r.desc || 'קריאת בטיחות'),
          website: r.website || null,
          sentAt: Number(r.sentAt || 0),
        });
      }
    }
    return out.sort((a, b) => b.sentAt - a.sentAt);
  } catch (_) { return []; }
}

/**
 * Load the maintenance log entries for the current vehicle.
 */
async function loadMaintenance(plate) {
  try {
    const snap = await ref(`vehicles/${plate}/maintenance`).once('value');
    const map = snap.val() || {};
    /** @type {Array<{ id: string, type: string, date: string, note: string, mileage: number | null, at: number }>} */
    const out = [];
    for (const [id, rec] of Object.entries(map)) {
      const r = /** @type {any} */ (rec);
      out.push({
        id,
        type: String(r.type || 'אחר'),
        date: String(r.date || ''),
        note: String(r.note || ''),
        mileage: r.mileage ? Number(r.mileage) : null,
        at: Number(r.at || 0),
      });
    }
    return out.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  } catch (_) { return []; }
}

/**
 * Quick activity stats derived from data we already have on the client.
 */
async function loadStats() {
  const uid = auth().currentUser?.uid;
  let searchCount = 0;
  let plateViews = 0;
  let openRecallsCount = 0;
  try {
    const hist = LocalState.searchHistory;
    if (Array.isArray(hist)) searchCount = hist.length;
  } catch (_) { /* ignore */ }
  if (uid) {
    try {
      const snap = await ref(`users/${uid}/searchHistory`).once('value');
      const map = snap.val() || {};
      searchCount = Math.max(searchCount, Object.keys(map).length);
    } catch (_) { /* ignore */ }
  }
  // Plate views counter — set by the search flow if it tracks views.
  try {
    const plate = LocalState.plate;
    if (plate) {
      const snap = await ref(`vehicles/${plate}/viewCount`).once('value');
      const v = snap.val();
      if (typeof v === 'number') plateViews = v;
    }
  } catch (_) { /* ignore */ }
  return { searchCount, plateViews, openRecallsCount };
}

// ====================================================================
// Rendering — main
// ====================================================================

function renderNoCar() {
  return `
    <div class="car-card">
      <div class="row" style="border-bottom:none;">
        <div style="font-weight:700; margin-bottom:6px;">אין רכב משויך</div>
        <div style="color:#6b7a89; font-size:13px;">ניתן לשייך רכב בחלון ייעודי.</div>
      </div>
    </div>
    <div class="car-card">
      <div class="row" style="border-bottom:none;display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <div style="font-weight:700;">שיוך רכב</div>
        <button id="open_replace_modal_btn" class="green-button" style="min-width:140px;">פתח חלון שיוך</button>
      </div>
    </div>
  `;
}

/**
 * @param {{
 *   model: MyCarModel,
 *   gov: any,
 *   ownership: { dateISO: string | null },
 *   license: { status: string, daysLeft: number | null, prettyDate?: string | null },
 *   recalls: Array<any>,
 *   maintenance: Array<any>,
 *   stats: { searchCount: number, plateViews: number },
 *   bannersId: string
 * }} args
 */
function renderHasCar({ model, gov, ownership, license, recalls, maintenance, stats, bannersId }) {
  return `
    ${renderHero(model, gov, ownership)}
    ${renderDashboard(license, ownership, recalls, gov)}
    ${renderRecalls(recalls)}
    <div class="car-card"><div id="${bannersId}" class="banners-stack"></div></div>
    ${renderDetailsCollapsible(gov, model)}
    ${renderMaintenance(maintenance)}
    ${renderStats(stats, recalls.length, ownership)}
    ${renderActions()}
  `;
}

// ====================================================================
// Section 1 — Hero card
// ====================================================================

function renderHero(model, gov, ownership) {
  const plateHtml = plateBadgeHtml(model.plate ?? '');
  const colorName = String(gov.tzeva_rechev || '').trim();
  const colorHex = colorNameToHex(colorName);
  const manufacturer = String(gov.tozeret_nm || '').trim();
  const commercialName = String(gov.kinuy_mishari || gov.degem_nm || '').trim();
  const fullModel = [manufacturer, commercialName].filter(Boolean).join(' ');
  const year = model.year || gov.shnat_yitzur || '';
  const ownedFor = ownership.dateISO ? ownedDurationText(ownership.dateISO) : '';

  return `
    <div class="mycar-hero">
      <div class="mycar-hero-icon" style="--car-color:${colorHex}">
        <svg viewBox="0 0 64 32" width="84" height="42" aria-hidden="true">
          <path fill="var(--car-color)" stroke="rgba(0,0,0,0.25)" stroke-width="1"
                d="M6 22 L10 12 Q12 8 16 8 L48 8 Q52 8 54 12 L58 22 L58 28 Q58 30 56 30 L52 30 Q50 30 50 28 L50 26 L14 26 L14 28 Q14 30 12 30 L8 30 Q6 30 6 28 Z"/>
          <circle cx="16" cy="27" r="4" fill="#222"/>
          <circle cx="48" cy="27" r="4" fill="#222"/>
          <circle cx="16" cy="27" r="1.8" fill="#888"/>
          <circle cx="48" cy="27" r="1.8" fill="#888"/>
          <path fill="rgba(255,255,255,0.4)" d="M14 14 L18 10 L46 10 L50 14 L46 18 L18 18 Z"/>
        </svg>
      </div>
      <div class="mycar-hero-text">
        <div class="mycar-hero-plate">${plateHtml}</div>
        ${fullModel ? `<div class="mycar-hero-model">${esc(fullModel)}</div>` : ''}
        ${year ? `<div class="mycar-hero-year">שנת ${esc(String(year))}</div>` : ''}
        ${ownedFor ? `<div class="mycar-hero-owned">${esc(ownedFor)}</div>` : ''}
      </div>
      <button id="car_refresh_btn" type="button"
              aria-label="רענן נתוני רכב מהמאגר"
              title="רענן מהמאגר"
              class="mycar-refresh-btn">
        <i class="fa-solid fa-arrows-rotate" aria-hidden="true"></i>
      </button>
    </div>
  `;
}

// ====================================================================
// Section 2 — Dashboard widgets
// ====================================================================

function renderDashboard(license, ownership, recalls, gov) {
  // 1) License widget
  let licColor = 'green', licValue = '—', licLabel = 'ימים';
  if (license.status === 'red') { licColor = 'red'; licValue = String(Math.abs(license.daysLeft ?? 0)); licLabel = 'ימים פג'; }
  else if (license.status === 'orange') { licColor = 'orange'; licValue = String(license.daysLeft ?? 0); licLabel = 'ימים'; }
  else if (license.status === 'green') { licColor = 'green'; licValue = String(license.daysLeft ?? 0); licLabel = 'ימים'; }
  else { licColor = 'gray'; licValue = '?'; licLabel = 'אין מידע'; }

  // 2) Ownership widget — show match status
  const ownColor = ownership.dateISO ? 'green' : 'gray';
  const ownLabel = ownership.dateISO ? 'תקין' : 'אין מידע';

  // 3) Recalls widget
  const rcColor = recalls.length === 0 ? 'green' : 'red';
  const rcLabel = recalls.length === 0 ? 'אין' : 'פתוחות';

  // 4) Pollution widget (kvutzat_zihum 1-15, lower = cleaner)
  const polRaw = parseInt(String(gov.kvutzat_zihum || '0'), 10);
  let polColor = 'gray', polValue = '?', polLabel = 'מקבוצה';
  if (polRaw > 0 && polRaw <= 7) { polColor = 'green'; polValue = String(polRaw); polLabel = 'נמוך'; }
  else if (polRaw > 7 && polRaw <= 11) { polColor = 'orange'; polValue = String(polRaw); polLabel = 'בינוני'; }
  else if (polRaw > 11) { polColor = 'red'; polValue = String(polRaw); polLabel = 'גבוה'; }

  return `
    <div class="mycar-dash">
      ${dashWidget('רישיון', dotColor(licColor), licValue, licLabel)}
      ${dashWidget('בעלות', dotColor(ownColor), '✓', ownLabel)}
      ${dashWidget('קריאות', dotColor(rcColor), String(recalls.length), rcLabel)}
      ${dashWidget('זיהום', dotColor(polColor), polValue, polLabel)}
    </div>
  `;
}

function dashWidget(title, dot, value, label) {
  return `
    <div class="mycar-widget">
      <div class="mycar-widget-title">${title}</div>
      <div class="mycar-widget-dot">${dot}</div>
      <div class="mycar-widget-value">${value}</div>
      <div class="mycar-widget-label">${label}</div>
    </div>
  `;
}

function dotColor(c) {
  return ({ green: '🟢', orange: '🟡', red: '🔴', gray: '⚪' })[c] || '⚪';
}

// ====================================================================
// Section 3 — Open recalls
// ====================================================================

function renderRecalls(recalls) {
  if (recalls.length === 0) return '';
  const items = recalls.map((rc) => `
    <div class="mycar-recall-item" data-recall-id="${esc(rc.id)}">
      <div class="mycar-recall-desc">${esc(rc.desc)}</div>
      <div class="mycar-recall-actions">
        ${rc.website
          ? `<a href="${esc(rc.website)}" target="_blank" rel="noopener" class="mycar-recall-link">פתח אתר היצרן ↗</a>`
          : ''}
        <button type="button" class="mycar-recall-handled-btn" data-recall-id="${esc(rc.id)}">
          ✓ סמן כמטופל
        </button>
      </div>
    </div>
  `).join('');

  return `
    <div class="car-card mycar-recalls-card">
      <div class="mycar-section-title">
        ⚠️ קריאות יצרן פתוחות (${recalls.length})
      </div>
      <div class="mycar-section-hint">
        קריאות בטיחות שמשרד התחבורה רשם — מומלץ לטפל בהן בקרוב.
      </div>
      <div class="mycar-recall-list">${items}</div>
    </div>
  `;
}

// ====================================================================
// Section 5 — Details (collapsible)
// ====================================================================

function renderDetailsCollapsible(gov, model) {
  const rows = [
    ['יצרן',          gov.tozeret_nm],
    ['דגם מסחרי',     gov.kinuy_mishari || gov.degem_nm],
    ['שנת ייצור',     gov.shnat_yitzur || model.year],
    ['צבע',           gov.tzeva_rechev],
    ['סוג דלק',       gov.sug_delek_nm],
    ['רמת גימור',     gov.ramat_gimur],
    ['קבוצת זיהום',   gov.kvutzat_zihum],
    ['רמת אבזור בטיחות', gov.ramat_eivzur_betihuty],
    ['צמיג קדמי',     gov.zmig_kidmi],
    ['צמיג אחורי',    gov.zmig_ahori],
    ['מספר שלדה',     gov.misgeret],
    ['טסט אחרון',     gov.mivchan_acharon_dt],
    ['עלה לכביש',     gov.moed_aliya_lakvish],
    ['בעלות',         gov.baalut],
  ].filter(([_, v]) => v != null && String(v).trim() !== '');

  if (rows.length === 0) return '';

  return `
    <details class="car-card mycar-details">
      <summary class="mycar-details-summary">
        <span class="mycar-section-title">📋 פרטי הרכב</span>
        <span class="mycar-details-chevron">▼</span>
      </summary>
      <div class="mycar-details-grid">
        ${rows.map(([k, v]) => `
          <div class="mycar-details-key">${esc(k)}</div>
          <div class="mycar-details-val">${esc(String(v))}</div>
        `).join('')}
      </div>
    </details>
  `;
}

// ====================================================================
// Section 6 — Maintenance log
// ====================================================================

function renderMaintenance(maintenance) {
  const list = maintenance.length === 0
    ? `<div class="mycar-maintenance-empty">עדיין לא נרשם טיפול. הוסיפו רשומה ראשונה למטה.</div>`
    : maintenance.map((m) => `
        <div class="mycar-maint-item">
          <div class="mycar-maint-row">
            <span class="mycar-maint-type">${esc(m.type)}</span>
            <span class="mycar-maint-date">${esc(m.date)}</span>
          </div>
          ${m.note ? `<div class="mycar-maint-note">${esc(m.note)}</div>` : ''}
          ${m.mileage ? `<div class="mycar-maint-mileage">${esc(String(m.mileage))} ק"מ</div>` : ''}
        </div>
      `).join('');

  return `
    <div class="car-card mycar-maintenance-card">
      <div class="mycar-section-title">🔧 יומן תחזוקה</div>
      <div class="mycar-maint-list">${list}</div>

      <details class="mycar-maint-form-wrap">
        <summary class="mycar-maint-add-summary">➕ הוסף רשומה</summary>
        <div class="mycar-maint-form">
          <select id="maint_type" class="mycar-input">
            <option value="טיפול קטן">טיפול קטן</option>
            <option value="טיפול גדול">טיפול גדול</option>
            <option value="החלפת שמן">החלפת שמן</option>
            <option value="החלפת בלמים">החלפת בלמים</option>
            <option value="החלפת צמיגים">החלפת צמיגים</option>
            <option value="טסט שנתי">טסט שנתי</option>
            <option value="פנצ'ר / תיקון מהיר">פנצ'ר / תיקון מהיר</option>
            <option value="תקלה אחרת">תקלה אחרת</option>
          </select>
          <input id="maint_date" type="date" class="mycar-input" />
          <input id="maint_mileage" type="number" class="mycar-input" placeholder="ק&quot;מ (לא חובה)" min="0" />
          <textarea id="maint_note" class="mycar-input" placeholder="הערות (לא חובה)" rows="2"></textarea>
          <button type="button" id="maint_save_btn" class="mycar-maint-save-btn">שמור רשומה</button>
        </div>
      </details>
    </div>
  `;
}

// ====================================================================
// Section 7 — Stats
// ====================================================================

function renderStats(stats, openRecallsCount, ownership) {
  const ownedDays = ownership.dateISO
    ? Math.max(0, Math.floor((Date.now() - Date.parse(ownership.dateISO)) / 86400000))
    : null;

  return `
    <div class="car-card mycar-stats-card">
      <div class="mycar-section-title">📊 הפעילות שלך</div>
      <div class="mycar-stats-grid">
        ${statTile('🔍', stats.searchCount, 'חיפושים סה"כ')}
        ${statTile('⚠️', openRecallsCount, 'קריאות פתוחות')}
        ${statTile('📅', ownedDays ?? '—', 'ימי בעלות')}
      </div>
    </div>
  `;
}

function statTile(icon, value, label) {
  return `
    <div class="mycar-stat">
      <div class="mycar-stat-icon">${icon}</div>
      <div class="mycar-stat-value">${esc(String(value))}</div>
      <div class="mycar-stat-label">${esc(label)}</div>
    </div>
  `;
}

// ====================================================================
// Section 8 — Bottom action buttons
// ====================================================================

function renderActions() {
  return `
    <div class="car-card" id="detailsBtnCard">
      <div class="row" style="border-bottom:none;display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <div style="font-weight:700;">פרטי רכב מלאים מהממשלה</div>
        <button id="car_details_btn" class="green-button" style="min-width:140px;">פתח</button>
      </div>
    </div>
    <div class="car-card">
      <div class="row" style="border-bottom:none;display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <div style="font-weight:700;">החלפת רכב משויך</div>
        <button id="open_replace_modal_btn" class="green-button" style="min-width:140px;">החלף עכשיו</button>
      </div>
    </div>
  `;
}

// ====================================================================
// Wiring (event handlers)
// ====================================================================

/** @param {HTMLElement} host */
function wireButtons(host) {
  const detailsBtn = host.querySelector('#car_details_btn');
  if (detailsBtn && !detailsBtn.hasAttribute('data-bound')) {
    detailsBtn.addEventListener('click', () => {
      const w = /** @type {any} */ (window);
      try { w.openVehicleDetailsModal?.(); }
      catch (e) { log.warn('openVehicleDetailsModal:', e); }
    });
    detailsBtn.setAttribute('data-bound', 'true');
  }
  const replaceBtn = host.querySelector('#open_replace_modal_btn');
  if (replaceBtn && !replaceBtn.hasAttribute('data-bound')) {
    replaceBtn.addEventListener('click', () => {
      const w = /** @type {any} */ (window);
      try { w.openReplaceModal?.(); }
      catch (e) { log.warn('openReplaceModal:', e); }
    });
    replaceBtn.setAttribute('data-bound', 'true');
  }
}

/**
 * Click → force gov refresh + ownership refresh + re-render banners.
 * Reuses the same refresh logic from the previous design.
 *
 * @param {HTMLElement} host
 * @param {string} plate
 * @param {string} bannersId
 */
function wireRefreshButton(host, plate, bannersId) {
  const btn = /** @type {HTMLButtonElement | null} */ (host.querySelector('#car_refresh_btn'));
  if (!btn || btn.hasAttribute('data-bound')) return;
  btn.setAttribute('data-bound', 'true');
  btn.addEventListener('click', async () => {
    if (btn.disabled) return;
    btn.disabled = true;
    const icon = btn.querySelector('i');
    if (icon) /** @type {HTMLElement} */ (icon).style.animation = 'spin 0.8s linear infinite';
    ensureSpinAnimation();
    try {
      await Promise.all([
        ensureGovBundle(plate, 0).catch((e) => log.warn('gov refresh:', e)),
        getLastOwnership(plate, 0).catch((e) => log.warn('ownership refresh:', e)),
      ]);
      // Re-render the entire section so widgets / details reflect new data.
      await renderSection();
      bus.emit('vehicle:changed', { plate });
    } catch (err) {
      log.warn('refresh button failed:', err);
    } finally {
      if (icon) /** @type {HTMLElement} */ (icon).style.animation = '';
      btn.disabled = false;
    }
  });
}

/** Wire "✓ סמן כמטופל" buttons for each open recall. */
function wireRecallActions(host, plate) {
  host.querySelectorAll('.mycar-recall-handled-btn').forEach((btn) => {
    if (btn.hasAttribute('data-bound')) return;
    btn.setAttribute('data-bound', 'true');
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-recall-id');
      if (!id) return;
      /** @type {HTMLButtonElement} */ (btn).disabled = true;
      try {
        await ref(`vehicles/${plate}/recallsNotified/${id}/markedHandledAt`).set(Date.now());
        await renderSection();
      } catch (e) {
        log.warn('mark recall handled failed:', e);
        /** @type {HTMLButtonElement} */ (btn).disabled = false;
      }
    });
  });
}

/** Wire the maintenance "save" button (inside the collapsed add-form). */
function wireMaintenanceForm(host, plate) {
  const btn = /** @type {HTMLButtonElement | null} */ (host.querySelector('#maint_save_btn'));
  if (!btn || btn.hasAttribute('data-bound')) return;
  btn.setAttribute('data-bound', 'true');

  // Default today's date in the date input.
  const dateInput = /** @type {HTMLInputElement | null} */ (host.querySelector('#maint_date'));
  if (dateInput && !dateInput.value) {
    dateInput.value = new Date().toISOString().slice(0, 10);
  }

  btn.addEventListener('click', async () => {
    const type    = /** @type {HTMLSelectElement} */   (host.querySelector('#maint_type'))?.value?.trim() ?? '';
    const date    = /** @type {HTMLInputElement} */    (host.querySelector('#maint_date'))?.value?.trim() ?? '';
    const note    = /** @type {HTMLTextAreaElement} */ (host.querySelector('#maint_note'))?.value?.trim() ?? '';
    const mileage = /** @type {HTMLInputElement} */    (host.querySelector('#maint_mileage'))?.value?.trim() ?? '';

    if (!type || !date) {
      btn.textContent = 'חסר סוג או תאריך';
      setTimeout(() => { btn.textContent = 'שמור רשומה'; }, 1800);
      return;
    }
    btn.disabled = true;
    btn.textContent = '⏳ שומר...';
    try {
      const r = ref(`vehicles/${plate}/maintenance`).push();
      await r.set({
        type, date, note,
        mileage: mileage ? Number(mileage) : null,
        at: Date.now(),
      });
      btn.textContent = '✅ נשמר';
      setTimeout(async () => {
        await renderSection();
      }, 500);
    } catch (e) {
      log.warn('save maintenance failed:', e);
      btn.textContent = '❌ שגיאה';
      setTimeout(() => { btn.textContent = 'שמור רשומה'; btn.disabled = false; }, 1800);
    }
  });
}

// ====================================================================
// Helpers
// ====================================================================

/** Map Hebrew-text colour names from CKAN to a CSS hex. Fuzzy / partial. */
function colorNameToHex(name) {
  if (!name) return '#94a3b8';
  const n = name.replace(/\s+/g, ' ').trim();
  const map = [
    [/לבן|שנהב/,  '#f3f4f6'],
    [/שחור/,      '#1f2937'],
    [/אפור/,      '#6b7280'],
    [/כסף|כסוף/,  '#cbd5e1'],
    [/אדום|בורדו|יין/, '#dc2626'],
    [/כחול/,      '#2563eb'],
    [/ירוק/,      '#16a34a'],
    [/צהוב/,      '#facc15'],
    [/כתום/,      '#f97316'],
    [/חום|בז/,    '#92400e'],
    [/סגול/,      '#7c3aed'],
    [/ורוד/,      '#ec4899'],
    [/זהב/,       '#d97706'],
  ];
  for (const [re, hex] of map) {
    if (/** @type {RegExp} */ (re).test(n)) return /** @type {string} */ (hex);
  }
  return '#94a3b8';
}

/** "ברשותך X חודשים" / "ברשותך Y שנים ו-Z חודשים" */
function ownedDurationText(dateISO) {
  const ms = Date.now() - Date.parse(dateISO);
  if (!Number.isFinite(ms) || ms < 0) return '';
  const totalMonths = Math.floor(ms / (30 * 86400000));
  if (totalMonths === 0) return 'ברשותך פחות מחודש';
  if (totalMonths < 12) return `ברשותך ${totalMonths} חודשים`;
  const y = Math.floor(totalMonths / 12);
  const m = totalMonths % 12;
  if (m === 0) return `ברשותך ${y} שנים`;
  return `ברשותך ${y} שנים ו-${m} חודשים`;
}

/** Inject a one-time `@keyframes spin` rule. Idempotent. */
function ensureSpinAnimation() {
  if (document.getElementById('mycar_refresh_spin')) return;
  const style = document.createElement('style');
  style.id = 'mycar_refresh_spin';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}
